import axios from 'axios';

// Add a response interceptor
axios.interceptors.response.use(function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
}, function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    //console.log(error, error.response);
    if (error.response.status === 401) {
        // redirect to login page.
        PricingAPI.auth.logout();
    }
    return Promise.reject(error);
});

export class PricingAPI {
    static auth = class {
        _appLoginedCallback = null;
        _tokenData = null;
        _userData = null;
        static getLoginUser = () => {
            return this._userData;
        };
        static getPermissions = () => {
            const user = this.getLoginUser();
            return (user && user.permissions) || [];
        };
        static updatePassword = (currentPassword, password, passwordConfirmation) => {
            return axios.post(`/auth/password`, {
                current_password: currentPassword,
                password: password,
                password_confirmation: passwordConfirmation,
            });
        };
        static init = (appLoginedCallback) => {
            this._appLoginedCallback = appLoginedCallback;
        }
        static login = (email, password) => {
            let data = new FormData();
            data.append('email', email);
            data.append('password', password);
            data.append('token_name', 'test');
            return axios.post(`/auth/login`, data).then((response) => {
                const token = response.data.token;
                this._tokenData = token;
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
                localStorage.setItem('token', token);
                return this.user().then((response) => {
                    this._appLoginedCallback(true);
                });
            });
        };
        static loginWithToken = async () => {
            if (this.getLoginUser()) return;
            const savedToken = localStorage.getItem('token');
            if (savedToken) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + savedToken;
                await this.user().then((response) => {
                    this._tokenData = savedToken;
                    this._appLoginedCallback(true);
                }).catch((error) => {
                    localStorage.removeItem('token');
                });
            }
        }
        static logout = () => {
            if (!this._tokenData && !this._userData) return;
            this._tokenData = null;
            this._userData = null;
            localStorage.removeItem('token');
            this._appLoginedCallback(false);
            return axios.post(`/auth/logout`);
        }
        static user = () => {
            return axios.get(`/auth/user`).then((response) => {
                this._userData = response.data;
                console.log(this._userData);
            });
        }
    }

    static users = class {
        static options = () => axios.get(`/users/options`)

        static index = (query, page, pageSize) => {
            return axios.get(`/users`, {
                params: {
                    page: page || 1,
                    count: pageSize || 30,
                    ...query
                },
            });
        }
        static show = (id) => axios.get(`/users/${id}`)
        static store = (data) => {
            return axios.post(`/users`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => {
            return axios.patch(`/users/${id}`, data);
        }

        static destroy = (id) => axios.delete(`/users/${id}`);
    };
    static invitations = class {
        static index = () => axios.get(`/invitations`);
        static store = (data) => axios.post(`/invitations`, data);
        static destroy = (id) => axios.delete(`/invitations/${id}`);
        // 以下兩支是公開端點，受邀者尚未登入時也會呼叫。token 刻意放在 body
        // 而不是網址上——網址會被 nginx access log 整段記下來，token 又帶
        // 7 天效期，日誌檔又是掛在主機上世界可讀
        static verify = (token) => axios.post(`/invitations/verify`, { token });
        static accept = (token, data) => axios.post(`/invitations/accept`, { token, ...data });
    };
    static loginLogs = class {
        static index = (query, page, pageSize) => {
            return axios.get(`/login-logs`, {
                params: {
                    page: page || 1,
                    count: pageSize || 50,
                    ...query
                },
            });
        }
    };
    static hotels = class {
        static index = (query, page, pageSize) => {
            return axios.get(`/hotels`, {
                params: {
                    page: page || 1,
                    count: pageSize || 30,
                    ...query
                },
            });
        }
        static show = (id) => axios.get(`/hotels/${id}`)
        static store = (data) => {
            return axios.post(`/hotels`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => {
            return axios.patch(`/hotels/${id}`, data);
        }

        static destroy = (id) => axios.delete(`/hotels/${id}`);
    };
    static roomcategories = class {
        static index = (query, page, pageSize) => {
            return axios.get(`/roomcategories`, {
                params: {
                    page: page || 1,
                    count: pageSize || 30,
                    ...query
                },
            });
        }
        static show = (id) => axios.get(`/roomcategories/${id}`)
        static store = (data) => {
            return axios.post(`/roomcategories`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => {
            return axios.patch(`/roomcategories/${id}`, data);
        }

        static destroy = (id) => axios.delete(`/roomcategories/${id}`);
    };
    static roomtypes = class {
        static index = (query, page, pageSize) => {
            return axios.get(`/roomtypes`, {
                params: {
                    page: page || 1,
                    count: pageSize || 30,
                    ...query
                },
            });
        }
        static show = (id) => axios.get(`/roomtypes/${id}`)
        static store = (data) => {
            return axios.post(`/roomtypes`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => {
            return axios.patch(`/roomtypes/${id}`, data);
        }

        static destroy = (id) => axios.delete(`/roomtypes/${id}`);
    };
    static roomprices = class {
        static index = (query, page, pageSize) => {
            return axios.get(`/roomprices`, {
                params: {
                    page: page || 1,
                    count: pageSize || 30,
                    ...query
                },
            });
        }
        static show = (id) => axios.get(`/roomprices/${id}`)
        static store = (data) => {
            return axios.post(`/roomprices`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => {
            return axios.patch(`/roomprices/${id}`, data);
        }

        static destroy = (id) => axios.delete(`/roomprices/${id}`);
    };
    static pricetypes = class {
        static index = (query, page, pageSize) => {
            return axios.get(`/roomtypes`, {
                params: {
                    page: page || 1,
                    count: pageSize || 30,
                    ...query
                },
            });
        }
        static show = (id) => axios.get(`/roomtypes/${id}`)
        static store = (data) => {
            return axios.post(`/roomtypes`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => {
            return axios.patch(`/roomtypes/${id}`, data);
        }

        static destroy = (id) => axios.delete(`/roomtypes/${id}`);
    };
    static mail = class {
        static recipitents = class {
            static index = (query, page, pageSize) => {
                return axios.get(`/mailrecipients`, {
                    params: {
                        page: page || 1,
                        count: pageSize || 30,
                        ...query
                    },
                });
            }
            static show = (id) => axios.get(`/mailrecipients/${id}`)
            // 收件者若是系統使用者，姓名本來就在資料庫裡，不必再打一次
            static suggestName = (address) => axios.get(`/mailrecipients/suggest-name`, { params: { address } })
            static store = (data) => {
                return axios.post(`/mailrecipients`, JSON.stringify(data), {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });
            }
            static update = (id, data) => {
                return axios.patch(`/mailrecipients/${id}`, data);
            }

            static destroy = (id) => axios.delete(`/mailrecipients/${id}`);
        }

        static groups = class {
            static index = () => axios.get(`/mailgroups`)
            static show = (id) => axios.get(`/mailgroups/${id}`)
            static store = (data) => {
                return axios.post(`/mailgroups`, JSON.stringify(data), {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });
            }
            static update = (id, data) => {
                return axios.patch(`/mailgroups/${id}`, data);
            }

            static destroy = (id) => axios.delete(`/mailgroups/${id}`);
        }

        static notificationTypes = class {
            static index = () => axios.get(`/notification-types`)
        }
    }

    static departments = class {
        static index = () => axios.get(`/departments`)
        static options = () => axios.get(`/departments/options`)
        static show = (id) => axios.get(`/departments/${id}`)
        static store = (data) => {
            return axios.post(`/departments`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => axios.patch(`/departments/${id}`, data);
        static destroy = (id) => axios.delete(`/departments/${id}`);
    }

    static todos = class {
        static index = (query, page, pageSize) => {
            return axios.get(`/todos`, {
                params: {
                    page: page || 1,
                    count: pageSize || 30,
                    ...query
                },
            });
        }
        static show = (id) => axios.get(`/todos/${id}`)
        static store = (data) => {
            return axios.post(`/todos`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static updateState = (id, { state, note }) => {
            return axios.post(`/todos/${id}/state`, {
                state: state,
                note: note
            });
        }

        static update = (id, data) => axios.patch(`/todos/${id}`, data);

        static summary = () => axios.get(`/todos/summary`);

        static agenda = () => axios.get(`/todos/agenda`);

        static claim = (id) => axios.post(`/todos/${id}/claim`);

        static release = (id) => axios.post(`/todos/${id}/release`);

        static destroy = (id) => axios.delete(`/todos/${id}`);

        static compliance = (params) => axios.get('/todos/compliance', { params })

        static notes = class {
            static index = (todoId, query, page, pageSize) => {
                return axios.get(`/todos/${todoId}/notes`, {
                    params: {
                        page: page || 1,
                        count: pageSize || 30,
                        ...query
                    },
                });
            }
            static show = (todoId, id) => axios.get(`/todos/${todoId}/notes/${id}`)
            static store = (todoId, data) => {
                return axios.post(`/todos/${todoId}/notes`, JSON.stringify(data), {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });
            }
            static update = (todoId, id, data) => {
                return axios.patch(`/todos/${todoId}/notes/${id}`, data);
            }
            static destroy = (todoId, id) => axios.delete(`/todos/${todoId}/notes/${id}`);
        }

        static occurrences = class {
            static store = (data) => {
                return axios.post('/todos/occurrences', JSON.stringify(data), {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });
            }
        }

        // 清單內容現在在新增／編輯待辦的表單設定 (走 todos.store / todos.update 的 checklist)；index、store、destroy 沒有 UI 呼叫端，保留是為了對應仍存在的後端端點，只有 update (勾選) 還在用
        static items = class {
            static index = (todoId) => axios.get(`/todos/${todoId}/items`)
            static store = (todoId, data) => {
                return axios.post(`/todos/${todoId}/items`, JSON.stringify(data), {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });
            }
            static update = (todoId, itemId, data) => axios.patch(`/todos/${todoId}/items/${itemId}`, data);
            static destroy = (todoId, itemId) => axios.delete(`/todos/${todoId}/items/${itemId}`);
        }
    }

    static todoTemplates = class {
        static index = () => axios.get('/todo-templates')
        static store = (data) => {
            return axios.post('/todo-templates', JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static update = (id, data) => {
            return axios.patch(`/todo-templates/${id}`, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        }
        static destroy = (id) => axios.delete(`/todo-templates/${id}`)
    }

    // 假日設定 (MySQL holidays 的查詢與人工維護)
    static holiday = class {
        // 後台一律帶 with_trashed，已移除的列在前端切換顯示，不必為此多打一次 API
        static index = (from, to, withTrashed = false) => {
            return axios.get(`/holidays`, {
                params: { from, to, with_trashed: withTrashed ? 1 : 0 },
            });
        }
        // 補班日獨立一支 —— 它是上班日，跟假日混在同一個回應裡，
        // 呼叫端拿錯就是直接的定價錯誤
        static workdays = (from, to) => {
            return axios.get(`/holidays/workdays`, { params: { from, to } });
        }
        static create = (data) => axios.post(`/holidays`, data);
        static remove = (date) => axios.delete(`/holidays/${date}`);
        static restore = (date) => axios.post(`/holidays/${date}/restore`);
        static fetch = () => axios.post(`/holidays/fetch`);
    };

    static settings = class {
        static index = () => axios.get(`/settings`);
        static update = (values) => axios.put(`/settings`, { values });
        static clear = (key) => axios.delete(`/settings/${key}`);
        // endpoint 由後端註冊表宣告，前端不組路徑 ——
        // 這樣新增一個領域動作完全不必改前端
        static action = (endpoint) => axios.post(`/${endpoint}`);
    }

    // SurveyCake 問卷金鑰。金鑰是每份問卷各一組，故獨立於通用設定之外
    static surveycakeSurveys = class {
        static index = () => axios.get(`/surveycake-surveys`);
        static create = (data) => axios.post(`/surveycake-surveys`, data);
        static update = (id, data) => axios.put(`/surveycake-surveys/${id}`, data);
        static remove = (id) => axios.delete(`/surveycake-surveys/${id}`);
    }
}